import {css} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import isArchived from 'archive/isArchived';
import {V2RequestsFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import {ActionMethod, ActionOrigin} from 'constants/AnalyticsConstants';
import {NOTEBOOK_SECTION} from 'constants/FileBrowserConstants';
import {StringIDFolder} from 'constants/FolderConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {SectionWrapper} from 'metadata/MetadataEditorInfoSection';
import listFolderSection from 'nav/sidepanel/listFolderSection';
import notifications from 'notifications';
import {ProcessWatchersField} from 'processes/process-details/ProcessWatchersField';
import EditableStudiesList from 'studies/studyable/EditableStudiesList';
import {isItemStudyable} from 'studies/studyable/StudyableUtils';
import {AvatarShape, AvatarSize} from 'ui-library/avatar/AvatarConstants';
import {AvatarWithName} from 'ui-library/avatar/AvatarWithName';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import * as Headers from 'ui-library/Headers';
import ParentLink from 'ui-library/ParentLink';
import {readableTime} from 'util/DateUtil';

import {updateRequestV2Submission} from '../network/RequestV2SubmissionDAO';
import {
  getRequestV2SubmissionDetailsFromApiResponse,
  RequestV2SubmissionDetails,
  writeRequestV2SubmissionUpdateToCache,
} from './RequestV2SubmissionDetailsQueryUtils';
import {REQUEST_V2_SUBMISSION_DETAILS_STYLES} from './RequestV2SubmissionDetailsStyles';

interface RequestV2SubmissionDetailsSectionProps {
  requestV2Submission: RequestV2SubmissionDetails;
  folder: StringIDFolder;
}

export function RequestV2SubmissionDetailsSection(
  props: RequestV2SubmissionDetailsSectionProps
): JSX.Element {
  return (
    <div className={classNames(css(REQUEST_V2_SUBMISSION_DETAILS_STYLES.wrapper), 'u-spacing-top')}>
      <Headers.Header2 className={css(REQUEST_V2_SUBMISSION_DETAILS_STYLES.sectionHeader)}>
        Details
      </Headers.Header2>
      <Row bufferRowReset={true}>
        <Col sm={3}>
          <SectionWrapper headerName="Creator" elementName="creator">
            <AvatarWithName
              avatarInfo={props.requestV2Submission.creator}
              size={AvatarSize.XXSMALL}
              shape={AvatarShape.CIRCLE}
              showTooltip={true}
            />
          </SectionWrapper>
        </Col>
        <Col sm={3}>
          <SectionWrapper headerName="Created" elementName="createdAt">
            <span>{readableTime(props.requestV2Submission.createdAt)}</span>
          </SectionWrapper>
        </Col>
        <Col sm={3}>
          <SectionWrapper headerName="ID" elementName="id">
            {props.requestV2Submission.displayId}
          </SectionWrapper>
        </Col>
        <Col sm={3}>
          <SectionWrapper headerName="Request type" elementName="requestType">
            {isInaccessible(props.requestV2Submission.definition)
              ? ''
              : props.requestV2Submission.definition.name}
          </SectionWrapper>
        </Col>
      </Row>
      <Row bufferRowReset={true}>
        <Col sm={3}>
          <SectionWrapper headerName="Project" elementName="project">
            <ParentLink
              icon={ModelTypeConstants.FOLDER}
              onClickParent={() => {
                listFolderSection(props.folder, NOTEBOOK_SECTION);
              }}
              parentName={props.requestV2Submission.folder.name}
              tooltipTitle="Show containing project folder"
            />
          </SectionWrapper>
        </Col>
        {BenchlingConfig.STUDIES_ENABLE && isItemStudyable(props.requestV2Submission) && (
          <Col sm={3}>
            <SectionWrapper headerName="Studies" elementName="studies">
              <EditableStudiesList
                studyable={props.requestV2Submission}
                isWritable={
                  props.requestV2Submission.permissions.writable && !isArchived(props.requestV2Submission)
                }
              />
            </SectionWrapper>
          </Col>
        )}
        <Col sm={3}>
          <SectionWrapper headerName="Watchers" elementName="watchers">
            <ProcessWatchersField
              watchers={props.requestV2Submission.watchers ?? []}
              onChange={async (changedValue) => {
                const response = await updateRequestV2Submission({
                  id: props.requestV2Submission.id,
                  watcherIds: changedValue.map(({id}) => id),
                  actionOrigin: ActionOrigin.WORKFLOWS_TASK_GROUP_PAGE,
                  actionMethod: ActionMethod.DIRECT_EDIT,
                });
                writeRequestV2SubmissionUpdateToCache(
                  getRequestV2SubmissionDetailsFromApiResponse(response.requestV2Submissions[0])
                );
                notifications.success('Updated watchers');
              }}
              isDisabled={
                isArchived(props.requestV2Submission) ||
                !isFeatureEnabled({
                  feature: V2RequestsFeature.V2_REQUESTS_EDIT,
                  item: props.requestV2Submission,
                })
              }
            />
          </SectionWrapper>
        </Col>
      </Row>
    </div>
  );
}
